import { onCLS, onFCP, onINP, onLCP, onTTFB, Metric } from "web-vitals";
import { MuseumObject } from "@/backend/types/domain";
import { ImageLoadTally } from "./imageLoadTally";

/**
 * Product analytics, sent as events to the GA4 property that index.html
 * already loads. See docs/analytics.md for what each event answers and the
 * one-time GA setup the parameters need before they appear in reports.
 *
 * Only the production host sends. Everywhere else events go to the console,
 * so local runs and deploy previews never land in the site's numbers; add
 * `?analytics_debug` to a preview URL to send them to GA's DebugView instead.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

type FilterAction = "add" | "remove";

/** How a detail view was reached. "link" is a hash change we did not start: back/forward, or a pasted URL. */
export type ArtifactViewSource =
  | "card"
  | "prev"
  | "next"
  | "deep_link"
  | "similar"
  | "link";

export type ApiEndpoint =
  | "search"
  | "timeline"
  | "artifact"
  | "similar"
  | "museums";

type NoParams = Record<string, never>;

/** Every event the site sends, and its parameters. */
type AnalyticsEvents = {
  // Feature use. The event name is the feature, so these count in GA's
  // Events report with no setup.
  search_submit: { search_term: string };
  load_more: { results_page: number };
  timeline_open: NoParams;
  timeline_filter: { date_range: string };
  map_open: NoParams;
  map_filter: { culture: string; filter_action: FilterAction };
  pill_filter: { culture: string; filter_action: FilterAction };
  filter_by_culture: { culture: string; source: "card" | "detail" };
  card_image_step: { method: "button" | "swipe" };
  filters_clear: NoParams;
  artifact_view: {
    source: ArtifactViewSource;
    museum?: string;
    culture?: string;
  };
  similar_open: NoParams;
  similar_loaded: { result_count: number; duration_ms: number };
  similar_click: NoParams;
  share: { method: "native" | "copy_link" | "x" | "instagram" };
  menu_click: { menu_item: string };
  subscribe_click: NoParams;
  support_click: NoParams;

  // Performance
  /** Navigation start to the first page of results, once per page load. */
  first_results: { duration_ms: number; result_count: number };
  /** Navigation start to the first artifact image on screen, once per page load. */
  first_image: { duration_ms: number };
  search_complete: {
    search_term?: string;
    results_page: number;
    result_count: number;
    duration_ms: number;
  };
  image_load_summary: {
    museum: string;
    images_requested: number;
    images_loaded: number;
    images_failed: number;
    images_fallback: number;
    image_load_ms: number;
  };
  /** CLS is unitless, so it is sent ×1000 to keep every metric_value an integer. */
  web_vital: {
    metric_name: Metric["name"];
    metric_value: number;
    metric_rating: Metric["rating"];
  };

  // Errors
  exception: { description: string; fatal: boolean };
  /** status 0 means no response at all: offline, blocked, or the function timed out. */
  api_error: { endpoint: ApiEndpoint; status: number };
};

type ParamValue = string | number | boolean;

const PRODUCTION_HOST = "artifacts-index.surulere.dev";
// GA4 truncates string parameters at 100 characters; trimming here keeps the
// console log honest about what is stored.
const MAX_PARAM_LENGTH = 100;

let sendMode: "send" | "debug" | "log" | undefined;

const getSendMode = () => {
  if (sendMode === undefined) {
    sendMode =
      window.location.hostname === PRODUCTION_HOST
        ? "send"
        : new URLSearchParams(window.location.search).has("analytics_debug")
          ? "debug"
          : "log";
  }
  return sendMode;
};

export function trackEvent<K extends keyof AnalyticsEvents>(
  name: K,
  ...args: AnalyticsEvents[K] extends NoParams ? [] : [AnalyticsEvents[K]]
) {
  const params = (args[0] ?? {}) as Record<string, ParamValue | undefined>;
  const cleaned: Record<string, ParamValue> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    cleaned[key] =
      typeof value === "string" ? value.slice(0, MAX_PARAM_LENGTH) : value;
  }

  const mode = getSendMode();
  if (mode !== "send") console.debug("[analytics]", name, cleaned);
  if (mode === "log") return;

  // Analytics must never be the thing that breaks the page.
  try {
    window.gtag?.(
      "event",
      name,
      mode === "debug" ? { ...cleaned, debug_mode: true } : cleaned,
    );
  } catch {}
}

/** Milliseconds since navigation start, which is what performance.now() counts from. */
const sinceNavigationStart = () => Math.round(performance.now());

// --- Search -----------------------------------------------------------------

let firstResultsReported = false;

export function trackSearchComplete(
  params: AnalyticsEvents["search_complete"],
) {
  trackEvent("search_complete", params);
  if (!firstResultsReported) {
    firstResultsReported = true;
    trackEvent("first_results", {
      duration_ms: sinceNavigationStart(),
      result_count: params.result_count,
    });
  }
}

/**
 * fetch, reporting a failed request — an error status, or no response at
 * all — before the caller decides what to do about it.
 */
export async function trackedFetch(
  endpoint: ApiEndpoint,
  input: string,
  init?: RequestInit,
): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(input, init);
  } catch (err) {
    trackEvent("api_error", { endpoint, status: 0 });
    throw err;
  }
  if (!response.ok) {
    trackEvent("api_error", { endpoint, status: response.status });
  }
  return response;
}

// --- Detail view ------------------------------------------------------------

let nextViewSource: ArtifactViewSource | null = null;

/**
 * Says how the next detail view is being opened. The view itself is counted
 * where the detail renders, because several paths reach it — some by setting
 * the URL hash and letting the hash listener load the artifact — and that is
 * the one place they all pass through exactly once.
 */
export function setArtifactViewSource(source: ArtifactViewSource) {
  nextViewSource = source;
}

export function trackArtifactView(obj: MuseumObject) {
  trackEvent("artifact_view", {
    source: nextViewSource ?? "link",
    museum: obj.dataSource?.museum,
    culture: obj.culture,
  });
  nextViewSource = null;
}

// --- Images -----------------------------------------------------------------

const imageTally = new ImageLoadTally(() => performance.now());
// Long enough to fold a page of images into one event per museum, short
// enough that most summaries go out while the visitor is still here rather
// than racing the page being closed.
const IMAGE_FLUSH_DELAY_MS = 10_000;
let imageFlushTimer: number | undefined;
let firstImageReported = false;

const flushImageLoads = () => {
  window.clearTimeout(imageFlushTimer);
  imageFlushTimer = undefined;
  for (const row of imageTally.drain()) {
    trackEvent("image_load_summary", row);
  }
};

const scheduleImageFlush = () => {
  if (imageFlushTimer === undefined) {
    imageFlushTimer = window.setTimeout(flushImageLoads, IMAGE_FLUSH_DELAY_MS);
  }
};

export type ImageLoadMeasurement = {
  loaded(usedFallback: boolean): void;
  failed(): void;
};

export function startImageLoad(museum: string | undefined): ImageLoadMeasurement {
  const load = imageTally.start(museum || "unknown");
  scheduleImageFlush();
  return {
    loaded: (usedFallback) => {
      if (!load.loaded(usedFallback)) return;
      scheduleImageFlush();
      if (!firstImageReported) {
        firstImageReported = true;
        trackEvent("first_image", { duration_ms: sinceNavigationStart() });
      }
    },
    failed: () => {
      if (load.failed()) scheduleImageFlush();
    },
  };
}

// --- Errors -----------------------------------------------------------------

// An error thrown on every render or every scroll would otherwise send
// thousands of identical events from one visitor.
const MAX_EXCEPTIONS_PER_PAGE = 10;
const reportedExceptions = new Set<string>();

const trackException = (description: string) => {
  if (
    reportedExceptions.has(description) ||
    reportedExceptions.size >= MAX_EXCEPTIONS_PER_PAGE
  ) {
    return;
  }
  reportedExceptions.add(description);
  trackEvent("exception", { description, fatal: false });
};

// --- Setup ------------------------------------------------------------------

const trackWebVital = (metric: Metric) => {
  trackEvent("web_vital", {
    metric_name: metric.name,
    metric_value: Math.round(
      metric.name === "CLS" ? metric.value * 1000 : metric.value,
    ),
    metric_rating: metric.rating,
  });
};

/** Call once, before the app renders. */
export function initAnalytics() {
  // Uncaught errors, including a render error that takes the React tree down:
  // with no error boundary, React rethrows it to the window.
  window.addEventListener("error", (event) => {
    const where = event.filename
      ? ` @ ${event.filename.split("/").pop()}:${event.lineno}:${event.colno}`
      : "";
    trackException(`${event.message || "Unknown error"}${where}`);
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason =
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason);
    trackException(`Unhandled rejection: ${reason}`);
  });

  // The last chance to send what is still batched.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushImageLoads();
  });
  window.addEventListener("pagehide", flushImageLoads);

  onLCP(trackWebVital);
  onINP(trackWebVital);
  onCLS(trackWebVital);
  onFCP(trackWebVital);
  onTTFB(trackWebVital);
}
